import { useState } from "react";
import { toast } from "sonner";
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, Upload, Plus, Star, Film } from "lucide-react";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { http, fileUrl, formatSoles } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const ORDER_STATUS_COLORS = {
  pendiente_pago: "bg-amber-100 text-amber-800", pago_por_verificar: "bg-sky-100 text-sky-800",
  pagado: "bg-emerald-100 text-emerald-800", en_preparacion: "bg-violet-100 text-violet-800",
  listo_entregar: "bg-teal-100 text-teal-800", entregado: "bg-neutral-200 text-neutral-700",
  cancelado: "bg-red-100 text-red-700",
};

export const QUOTE_STATUS = [
  { value: "pendiente", label: "Pendiente", cls: "bg-amber-100 text-amber-800" },
  { value: "enviada", label: "Enviada", cls: "bg-sky-100 text-sky-800" },
  { value: "aceptada", label: "Aceptada", cls: "bg-emerald-100 text-emerald-800" },
  { value: "rechazada", label: "Rechazada", cls: "bg-red-100 text-red-700" },
];
export const quoteStatus = (v) => QUOTE_STATUS.find((s) => s.value === (v === "nuevo" ? "pendiente" : v)) || QUOTE_STATUS[0];

export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric" }) : "—");
export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("es-PE", { dateStyle: "short", timeStyle: "short" }) : "—");

export function Pill({ className = "", children, ...rest }) {
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold ${className}`} {...rest}>{children}</span>;
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold" data-testid="admin-page-title">{title}</h1>
        {subtitle && <p className="text-sm text-neutral-500 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Panel({ title, children, className = "", actions }) {
  return (
    <section className={`bg-white rounded-2xl border border-black/5 p-5 sm:p-6 ${className}`}>
      {(title || actions) && (
        <div className="flex items-center justify-between mb-4 gap-2">
          {title && <h3 className="font-serif text-xl font-bold">{title}</h3>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function Confirm({ trigger, title = "¿Confirmar eliminación?", description = "Esta acción no se puede deshacer.", onConfirm, confirmText = "Eliminar" }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel">Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-[var(--brand-primary)] hover:bg-red-700" data-testid="confirm-ok">{confirmText}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function SortableRow({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : "auto" }}
      className={`flex items-stretch gap-2 ${isDragging ? "opacity-80 shadow-xl" : ""}`}>
      <button type="button" {...attributes} {...listeners} className="touch-none px-1 text-neutral-400 hover:text-neutral-700 cursor-grab active:cursor-grabbing" aria-label="Arrastrar" data-testid={`drag-${id}`}>
        <GripVertical size={18} />
      </button>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

export function SortableList({ items, getId = (i) => i.id, onReorder, renderItem, className = "space-y-2" }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = items.map(getId);
  function onDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;
    onReorder(arrayMove(items, ids.indexOf(active.id), ids.indexOf(over.id)));
  }
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className={className}>
          {items.map((it, i) => <SortableRow key={getId(it)} id={getId(it)}>{renderItem(it, i)}</SortableRow>)}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export async function uploadFile(file, folder = "admin") {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("folder", folder);
  const { data } = await http.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return `/api/files/${data.path}`;
}

export function MediaList({ value = [], onChange, video = false, testid = "media" }) {
  const [busy, setBusy] = useState(false);
  async function up(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const urls = [];
    for (const f of files) {
      try { urls.push(await uploadFile(f, video ? "videos" : "products")); } catch { toast.error(`Error al subir ${f.name}`); }
    }
    onChange([...value, ...urls]);
    if (urls.length) toast.success(`${urls.length} archivo(s) subido(s)`);
    setBusy(false);
    e.target.value = "";
  }
  const makeMain = (i) => { const a = [...value]; const [x] = a.splice(i, 1); onChange([x, ...a]); };
  const replace = async (i, file) => {
    try { const url = await uploadFile(file, video ? "videos" : "products"); const a = [...value]; a[i] = url; onChange(a); toast.success("Reemplazado"); } catch { toast.error("Error al subir"); }
  };
  return (
    <div className="space-y-3" data-testid={`${testid}-list`}>
      <div className="flex flex-wrap gap-2">
        {value.map((src, i) => (
          <div key={src + i} className="relative" data-testid={`${testid}-item-${i}`}>
            {video ? (
              <div className="h-20 w-28 rounded-lg bg-neutral-900 text-white flex items-center justify-center"><Film size={20} /></div>
            ) : (
              <img src={fileUrl(src)} alt="" className={`h-20 w-20 rounded-lg object-cover border-2 ${i === 0 ? "border-[var(--brand-primary)]" : "border-transparent"}`} />
            )}
            {!video && i === 0 && <span className="absolute bottom-1 left-1 text-[9px] font-bold bg-[var(--brand-primary)] text-white px-1.5 rounded">Principal</span>}
            <div className="absolute top-1 right-1 flex gap-1">
              {!video && i !== 0 && (
                <button type="button" onClick={() => makeMain(i)} className="h-6 w-6 bg-white rounded shadow flex items-center justify-center" title="Hacer principal" data-testid={`${testid}-main-${i}`}><Star size={11} /></button>
              )}
              <label className="h-6 w-6 bg-white rounded shadow flex items-center justify-center cursor-pointer" title="Reemplazar">
                <Upload size={11} />
                <input type="file" accept={video ? "video/*" : "image/*"} className="hidden" onChange={(e) => e.target.files?.[0] && replace(i, e.target.files[0])} />
              </label>
              <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="h-6 w-6 bg-white rounded shadow flex items-center justify-center" title="Quitar" data-testid={`${testid}-remove-${i}`}><Trash2 size={11} /></button>
            </div>
          </div>
        ))}
      </div>
      <label className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-100 rounded-full cursor-pointer text-sm font-semibold hover:bg-neutral-200">
        <Upload size={14} /> {busy ? "Subiendo..." : video ? "Subir videos" : "Subir fotos"}
        <input type="file" accept={video ? "video/*" : "image/*"} multiple className="hidden" onChange={up} data-testid={`${testid}-input`} />
      </label>
    </div>
  );
}

export function SingleImage({ value, onChange, testid = "single-image", folder = "settings" }) {
  const [busy, setBusy] = useState(false);
  async function up(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { onChange(await uploadFile(f, folder)); toast.success("Imagen subida"); } catch { toast.error("Error al subir"); }
    setBusy(false);
  }
  return (
    <div className="flex items-center gap-3">
      {value ? <img src={fileUrl(value)} alt="" className="h-16 w-16 rounded-lg object-cover border" /> : <div className="h-16 w-16 rounded-lg bg-neutral-100" />}
      <label className="inline-flex items-center gap-2 px-3 py-2 bg-neutral-100 rounded-full cursor-pointer text-xs font-semibold hover:bg-neutral-200">
        <Upload size={12} /> {busy ? "Subiendo..." : value ? "Reemplazar" : "Subir"}
        <input type="file" accept="image/*" className="hidden" onChange={up} data-testid={testid} />
      </label>
      {value && <button type="button" className="text-xs text-neutral-500 hover:text-red-600" onClick={() => onChange("")}>Quitar</button>}
    </div>
  );
}

export function ItemsEditor({ items = [], onChange, products = [] }) {
  const [pick, setPick] = useState("");
  const update = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: k === "qty" || k === "unit_price" ? Number(v) : v } : it)));
  function addProduct(pid) {
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    onChange([...items, { product_id: p.id, code: p.code, name: p.name, qty: 1, unit_price: p.promo_price || p.retail_price }]);
    setPick("");
  }
  const addCustom = () => onChange([...items, { product_id: `custom-${Date.now()}`, code: "PERS", name: "Producto personalizado", qty: 1, unit_price: 0, custom: true }]);
  return (
    <div className="space-y-2" data-testid="items-editor">
      <div className="hidden sm:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-neutral-400 font-semibold px-1">
        <div className="col-span-2">Código</div><div className="col-span-4">Producto</div><div className="col-span-1">Cant.</div><div className="col-span-2">P. unit.</div><div className="col-span-2 text-right">Subtotal</div>
      </div>
      {items.map((it, i) => (
        <div key={it.product_id + i} className="grid grid-cols-12 gap-2 items-center bg-neutral-50 rounded-xl p-2" data-testid={`item-row-${i}`}>
          <Input className="col-span-4 sm:col-span-2 h-9 text-xs" value={it.code || ""} onChange={(e) => update(i, "code", e.target.value)} />
          <Input className="col-span-8 sm:col-span-4 h-9 text-sm" value={it.name} onChange={(e) => update(i, "name", e.target.value)} data-testid={`item-name-${i}`} />
          <Input className="col-span-3 sm:col-span-1 h-9 text-sm" type="number" min={1} value={it.qty} onChange={(e) => update(i, "qty", e.target.value)} data-testid={`item-qty-${i}`} />
          <Input className="col-span-4 sm:col-span-2 h-9 text-sm" type="number" step="0.01" value={it.unit_price} onChange={(e) => update(i, "unit_price", e.target.value)} data-testid={`item-price-${i}`} />
          <div className="col-span-3 sm:col-span-2 text-right text-sm font-semibold whitespace-nowrap">{formatSoles((it.qty || 0) * (it.unit_price || 0))}</div>
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="col-span-2 sm:col-span-1 flex justify-end text-neutral-400 hover:text-red-600" data-testid={`item-remove-${i}`}><Trash2 size={16} /></button>
          {(it.variant || it.color) && <div className="col-span-12 text-xs text-neutral-500 px-1">{[it.variant, it.color].filter(Boolean).join(" · ")}</div>}
        </div>
      ))}
      <div className="flex flex-wrap gap-2 pt-1">
        <select value={pick} onChange={(e) => addProduct(e.target.value)} className="h-9 rounded-md border border-input bg-white px-3 text-sm max-w-xs" data-testid="items-add-product">
          <option value="">+ Agregar producto del catálogo…</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.name}</option>)}
        </select>
        <Button type="button" variant="outline" size="sm" onClick={addCustom} data-testid="items-add-custom"><Plus size={14} className="mr-1" /> Producto personalizado</Button>
      </div>
    </div>
  );
}

export function exportXlsx(rows, filename, sheet = "Datos") {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheet);
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

export function makePdf({ settings, title, code, date, infoRows = [], items = [], totals = [], notes, mode = "download" }) {
  const doc = new jsPDF();
  const red = [229, 28, 42];
  doc.setFontSize(20).setTextColor(...red).text(settings?.business_name || "Floral Rossé", 14, 20);
  doc.setFontSize(9).setTextColor(90).text([settings?.whatsapp_number, settings?.address].filter(Boolean).join("  ·  "), 14, 26);
  doc.setFontSize(14).setTextColor(30).text(`${title} ${code || ""}`, 196, 20, { align: "right" });
  doc.setFontSize(9).setTextColor(90).text(date || "", 196, 26, { align: "right" });
  autoTable(doc, { startY: 34, body: infoRows, theme: "plain", styles: { fontSize: 9, cellPadding: 1 }, columnStyles: { 0: { fontStyle: "bold", cellWidth: 45 } } });
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 6,
    head: [["Código", "Producto", "Cant.", "P. unit.", "Subtotal"]],
    body: items.map((i) => [i.code || "", `${i.name}${i.variant ? ` (${i.variant})` : ""}${i.color ? ` [${i.color}]` : ""}`, i.qty, formatSoles(i.unit_price), formatSoles(i.qty * i.unit_price)]),
    headStyles: { fillColor: red }, styles: { fontSize: 9 },
  });
  autoTable(doc, { startY: doc.lastAutoTable.finalY + 4, body: totals, theme: "plain", margin: { left: 120 }, styles: { fontSize: 10, halign: "right" }, columnStyles: { 0: { fontStyle: "bold" } } });
  if (notes) {
    doc.setFontSize(9).setTextColor(60).text(doc.splitTextToSize(`Observaciones: ${notes}`, 180), 14, doc.lastAutoTable.finalY + 10);
  }
  if (mode === "print") {
    doc.autoPrint();
    window.open(doc.output("bloburl"), "_blank");
  } else {
    doc.save(`${code || title}.pdf`);
  }
}
